import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/Cliff Youth Center/images";

export default function CliffYouthCenterPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="Cliff Youth Center"
          description="A public library and youth centre at an underused urban node in Antalya — combining an adaptive louver facade with a pedestrian ramp that crosses the rainwater canal beside it."
          meta={["Antalya, Turkey", "Revit", "Dynamo", "Grasshopper", "Honeybee", "Galápagos", "2021"]}
        />

        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/Card_Louvers.png`}
                alt="Cliff Youth Center — exterior render"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        <BlockSection title="Project scope">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The site is an underused urban node with strong latent potential — pedestrian circulation cut by a rainwater canal and the rocky cliff topography behind it. The project reactivates the node by hosting a public library and youth centre, and by adding a pedestrian ramp that crosses the canal, separating foot traffic from the adjacent road.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/last site plan.jpg`} alt="Cliff Youth Center — site plan" ratio="classic" fit="contain" caption="Site plan" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/last plan.jpg`} alt="Cliff Youth Center — floor plan" ratio="classic" fit="contain" caption="Floor plan" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/last section.jpg`} alt="Cliff Youth Center — section" ratio="banner" fit="contain" caption="Section" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Facade system">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The facade was modelled parametrically in Revit and Dynamo. Each portion sits at a different angle responding to building form and pedestrian circulation; the same Dynamo definition drives the adaptive louver geometry and produces the CNC fabrication files for the cladding panels.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Facade Making Diagram.png`} alt="Facade making diagram" ratio="classic" fit="contain" caption="Facade construction logic" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Facade systemCNC Pattern.png`} alt="CNC pattern for facade panels" ratio="classic" fit="contain" caption="CNC panel pattern" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Adaptive louvers optimisation">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Louver angles were optimised inside Grasshopper using Galápagos as the genetic algorithm. For each facade portion, the engine iterated through combinations of angles and ran a Daylight Autonomy (DA) simulation in Honeybee/Radiance.",
              "The chosen configuration reaches 82% DA — up from 44% without the adaptive system.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Animated Parameters.gif`} alt="Animated louver parameters" ratio="classic" fit="contain" caption="Parameter sweep" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Animated Graph.gif`} alt="Daylight Autonomy optimisation graph" ratio="classic" fit="contain" caption="DA optimisation" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Animated Mesh.gif`} alt="Animated daylight mesh" ratio="banner" fit="contain" caption="Daylight mesh — Jun 21, 12:00" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Architectural system">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/ArchSystem.png`} alt="Architectural system overview" ratio="classic" fit="contain" caption="System overview" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Louvers.png`} alt="Louver system axonometric" ratio="classic" fit="contain" caption="Louver assembly" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        <BlockSection title="Renders and visualisations">
          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure src={`${BASE}/Image10.png`} alt="Cliff Youth Center render 1" ratio="classic" fit="cover" />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Image10_000.png`} alt="Cliff Youth Center render 2" ratio="classic" fit="cover" />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={12} className="!p-0 !border-r-0">
                <BlockFigure src={`${BASE}/Image5_002.png`} alt="Cliff Youth Center — wide context render" ratio="banner" fit="cover" />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>
      </div>
    </main>
  );
}
